<template>
  <!-- Exploration Modal -->
  <div v-if="gameState.inExplorationModal" class="exploration-modal" @click="closeModal">
    <div class="exploration-card" @click.stop>
      <!-- Fixed Header -->
      <div class="exploration-header">
        <h3>{{ currentScene.name }}</h3>
        <p v-if="isFirstExploration">{{ currentScene.description }}</p>
        <p v-else class="exploration-continue">继续探索中...</p>
        <div v-if="explorationState && newClues.length > 0" class="clues-section">
          <h4>新线索：</h4>
          <ul>
            <li v-for="clue in newClues" :key="clue">{{ clue }}</li>
          </ul>
        </div>
      </div>

      <!-- Scrollable Content -->
      <div class="exploration-content">
        <!-- Random Events Section -->
        <div v-if="triggeredEvent" class="random-event-section">
          <h4>🎯 特殊事件触发</h4>
          <div class="event-card">
            <h5>{{ triggeredEvent.name }}</h5>
            <p>{{ triggeredEvent.description }}</p>
            <div class="event-narrative">{{ triggeredEvent.narrative }}</div>
            <div v-if="eventResults.length > 0" class="event-results">
              <div v-for="result in eventResults" :key="result" class="event-result">{{ result }}</div>
            </div>
          </div>
          <button @click="continueExploration" class="continue-btn">继续探索</button>
        </div>

        <!-- Exploration Results -->
        <div v-if="explorationResults.length > 0" class="results-section">
          <h4>探索结果：</h4>
          <div class="result-messages">
            <div
              v-for="(result, index) in explorationResults"
              :key="index"
              :class="['result-message', { 'item-gained': isItemGainMessage(result) }]"
            >
              <span v-if="isItemGainMessage(result)" class="item-icon">🎒</span>
              {{ getResultText(result) }}
            </div>
          </div>
          <button @click="continueExploration" class="continue-btn">继续探索</button>
        </div>

        <!-- Normal Exploration Actions -->
        <div v-else class="actions-section">
          <h4>你可以：</h4>
          <div class="action-buttons">
            <button
              v-for="(action, index) in availableActions"
              :key="`action-${index}`"
              @click="handleActionClick(action, index + 1)"
              class="action-button"
              :disabled="isProcessing"
            >
              <span class="action-number">{{ index + 1 }}.</span>
              <span class="action-text">{{ action }}</span>
            </button>
          </div>
          <button @click="endExploration" class="end-btn">结束探索</button>
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="exploration-footer">
        <div class="exploration-stats">
          <span>探索心情：{{ explorationState ? explorationState.mood : '未知' }}</span>
          <span>发现数量：{{ explorationState ? explorationState.discovered.length : 0 }}</span>
        </div>
        <button @click="closeModal" class="close-btn">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import { LOCATION_NAMES, explorationScenes } from './exploration-data.js';

export default {
  name: 'ExplorationModal',
  props: {
    gameState: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      triggeredEvent: null,
      eventResults: [],
      explorationResults: [],
      isProcessing: false
    };
  },
  computed: {
    currentScene() {
      const scene = this.gameState.explorationState && this.gameState.explorationState.scene;
      if (scene && scene.name !== LOCATION_NAMES.UNKNOWN) {
        return scene;
      }
      // 如果还没有正确的场景，尝试重新初始化
      if (this.gameState.explorationState && !scene) {
        // 异步重新初始化场景
        this.$nextTick(() => {
          if (this.gameState.explorationManager) {
            const newScene = this.gameState.explorationManager.getLocationScene(this.gameState.location || '洞府');
            if (newScene && this.gameState.explorationState) {
              this.gameState.explorationState.scene = newScene;
              this.$forceUpdate();
            }
          }
        });
      }
      return explorationScenes[LOCATION_NAMES.UNKNOWN];
    },
    explorationState() {
      return this.gameState.explorationState;
    },
    isFirstExploration() {
      return this.explorationState && this.explorationState.explorationCount <= 1;
    },
    newClues() {
      if (!this.explorationState || !this.explorationState.clues) return [];
      return this.explorationState.clues.filter(clue =>
        !this.explorationState.previousClues?.includes(clue)
      );
    },
    availableActions() {
      // 确保返回数组，支持新旧两种数据格式
      const scene = this.currentScene;
      if (scene) {
        // 优先使用新格式 baseEvents
        if (scene.baseEvents && Array.isArray(scene.baseEvents)) {
          return scene.baseEvents;
        }
        // 向后兼容旧格式 actions
        if (scene.actions && Array.isArray(scene.actions)) {
          const actionTexts = scene.actions.map(action => action.description || action);
          return actionTexts;
        }
      }
      return [];
    }
  },
  methods: {
    isItemGainMessage(message) {
      // 防御性检查，确保message不为null/undefined
      if (!message) return false;

      // 处理Vue包装对象
      const actualMessage = message._wrapper ? message._wrapper : message;

      return typeof actualMessage === 'object' && actualMessage.isItemGain === true;
    },

    closeModal() {
      this.resetModalState();
      this.$emit('close-modal');
    },

    resetModalState() {
      this.triggeredEvent = null;
      this.eventResults = [];
      this.explorationResults = [];
    },

    handleActionClick(action, choiceNum) {
      if (this.isProcessing) return;
      this.isProcessing = true;
      this.$emit('exploration-choice', choiceNum.toString());
      this.isProcessing = false;
    },

    continueExploration() {
      this.resetModalState();
      this.$emit('continue-exploration');
    },

    endExploration() {
      this.$emit('end-exploration');
      this.closeModal();
    },

    getResultText(result) {
      if (!result) return '';
      return typeof result === 'object' ? (result.message || String(result)) : String(result);
    }
  },
  watch: {
    'gameState.inExplorationModal'(newVal) {
      if (newVal) {
        this.$emit('start-exploration');
        // Check for random events after initialization
        setTimeout(() => {
          if (this.gameState.explorationManager) {
            const result = this.gameState.explorationManager.checkAndTriggerRandomEvent();
            if (result) {
              this.triggeredEvent = result.event;
              this.eventResults = result.results || [];
            }
          }
        }, 200);
      }
    }
  }
};
</script>

<style scoped>
.exploration-modal {
  position: absolute;
  inset: 0px 0px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.8);
  z-index: 250;
  backdrop-filter: blur(2px);
}

.exploration-card {
  background: #071328;
  border-radius: 12px;
  border: 1px solid #334155;
  color: #e6eef8;
  min-width: 500px;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.exploration-header {
  flex-shrink: 0;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #334155;
}

.exploration-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.exploration-footer {
  flex-shrink: 0;
  padding: 16px 20px 20px 20px;
  border-top: 1px solid #334155;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.exploration-card h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #60a5fa;
  text-align: center;
}

.exploration-card p {
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  text-align: center;
  line-height: 1.5;
}

.exploration-continue {
  color: #60a5fa !important;
  font-style: italic;
  font-size: 12px !important;
}



.clues-section {
  margin-top: 12px;
  padding: 12px;
  background: rgba(96, 165, 250, 0.1);
  border-radius: 6px;
  border: 1px solid #60a5fa;
}

.clues-section h4 {
  margin: 0 0 8px 0;
  color: #60a5fa;
  font-size: 14px;
}

.clues-section ul {
  margin: 0;
  padding-left: 20px;
}

.clues-section li {
  color: #94a3b8;
  font-size: 13px;
  margin-bottom: 4px;
}

.random-event-section {
  margin-bottom: 20px;
}

.random-event-section h4 {
  color: #fbbf24;
  margin: 0 0 12px 0;
  text-align: center;
  font-size: 16px;
}

.event-card {
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.event-card h5 {
  margin: 0 0 8px 0;
  color: #fbbf24;
  font-size: 16px;
}

.event-card p {
  margin: 0 0 12px 0;
  color: #d97706;
  font-size: 14px;
  font-weight: 500;
}

.event-narrative {
  color: #f59e0b;
  font-style: italic;
  margin-bottom: 12px;
  line-height: 1.4;
}

.event-results {
  border-top: 1px solid rgba(251, 191, 36, 0.3);
  padding-top: 12px;
}

.event-result {
  color: #10b981;
  font-size: 13px;
  margin-bottom: 4px;
  padding: 4px 0;
}

.results-section {
  margin-bottom: 20px;
}

.results-section h4 {
  color: #60a5fa;
  margin: 0 0 12px 0;
  text-align: center;
  font-size: 16px;
}

.result-messages {
  background: rgba(96, 165, 250, 0.1);
  border: 1px solid #60a5fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.result-message {
  color: #e6eef8;
  font-size: 14px;
  margin-bottom: 8px;
  line-height: 1.4;
  padding: 4px 0;
}

.result-message:last-child {
  margin-bottom: 0;
}

.result-message.item-gained {
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid #22c55e;
  border-radius: 6px;
  padding: 8px 12px !important;
  margin-bottom: 8px;
  font-weight: 500;
  color: #bbf7d0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-icon {
  font-size: 16px;
  color: #22c55e;
  flex-shrink: 0;
}

.continue-btn {
  display: block;
  width: 100%;
  padding: 10px;
  background: #16a34a;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.continue-btn:hover {
  background: #15803d;
}

.actions-section h4 {
  margin: 0 0 12px 0;
  color: #60a5fa;
  text-align: center;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.action-button {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 2px solid #475569;
  background: rgba(71, 85, 105, 0.2);
  color: #e6eef8;
  cursor: pointer;
  border-radius: 8px;
  text-align: left;
  transition: all 0.3s ease;
}

.action-button:hover:not(:disabled) {
  border-color: #60a5fa;
  background: rgba(96, 165, 250, 0.3);
  transform: translateY(-1px);
}

.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.action-number {
  color: #60a5fa;
  font-weight: bold;
  margin-right: 8px;
  min-width: 20px;
}

.action-text {
  flex: 1;
}

.end-btn {
  display: block;
  width: 100%;
  padding: 10px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.end-btn:hover {
  background: #b91c1c;
}

.exploration-stats {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #94a3b8;
}

.close-btn {
  padding: 8px 16px;
  border: 1px solid #dc2626;
  background: transparent;
  color: #dc2626;
  cursor: pointer;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #dc2626;
  color: white;
}

/* 适配移动设备 */
@media (max-width: 900px) {
  .exploration-card {
    min-width: 90vw;
    max-width: 90vw;
  }

  .exploration-header {
    padding: 16px 16px 12px 16px;
  }

  .exploration-content {
    padding: 12px 16px;
  }

  .exploration-footer {
    padding: 12px 16px 16px 16px;
    flex-direction: column;
    gap: 12px;
  }

  .action-button {
    padding: 10px 12px;
  }

  .action-number {
    min-width: 16px;
    margin-right: 6px;
  }

  .exploration-stats {
    justify-content: center;
  }
}
</style>
